/* eslint-disable  @typescript-eslint/no-explicit-any */
import * as React from "react";
import { useEffect, useState } from "react";
import { AlertColor } from "@mui/material";
import { AppContext } from "../../../utils/AppContext";
import { LoadingOverlay } from "../../../components/LoadingOverlay";
import { Notify } from "../../../components/Notify";
import { YesNoCancelPopup } from "../../../components/YesNoCancelPopup";
import { IMaintenanceFile, useMaintenanceService } from "../../../services/registry/MaintenanceService";
import type { IEnvironment } from "../../../types/EnvironmentTypes";
import { MaintenanceMessage } from "./MaintenanceMessage";
import MaintenanceTemplatesTable from "./MaintenanceTemplatesTable";
import MaintenanceMessageForm from "./MaintenanceMessageForm";
import { getEnvironmentStageData } from "../../../services/registry/EnvironmentService";

const MaintenanceManagement = () => {
  const maintenanceService = useMaintenanceService();

  const [loading, setLoading] = useState(true);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [alertSeverity, setAlertSeverity] = useState<AlertColor>("info");
  const [maintenanceMessage, setMaintenanceMessage] = useState<IMaintenanceFile | undefined>(undefined);
  const [formVisible, setFormVisible] = useState(false);
  const [selectedEnvironment, setSelectedEnvironment] = useState<IEnvironment | undefined>(undefined);
  const [applyTemplatePopupVisible, setApplyTemplatePopupVisible] = useState(false);
  const [templateToApply, setTemplateToApply] = useState<string | undefined>(undefined);
  const [startMaintenancePopupVisible, setStartMaintenancePopupVisible] = useState(false);
  const [endMaintenancePopupVisible, setEndMaintenancePopupVisible] = useState(false);

  const context = React.useContext(AppContext);
  useEffect(() => {
    context?.setTitle("Maintenance Message");
    context?.setTargetAPI("registry");
  }, [context]);

  useEffect(() => {
    (async () => fetchMaintenanceMessage())();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchMaintenanceMessage = async () => {
    try {
      setLoading(true);
      setMaintenanceMessage(await maintenanceService.getMaintenanceMessage());
      setLoading(false);
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Failed to get maintenance message.");
      setAlertSeverity("error");
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = () => {
    setFormVisible(true);
  };

  const handleFormAccept = async (data: IMaintenanceFile) => {
    try {
      setLoading(true);
      await maintenanceService.updateMaintenanceMessage(data);
      setAlertMessage("Maintenance message updated successfully.");
      setAlertSeverity("success");
      await fetchMaintenanceMessage();
      setFormVisible(false);
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Failed to update maintenance message.");
      setAlertSeverity("error");
    } finally {
      setLoading(false);
    }
  };

  const handleFormDismiss = () => {
    setFormVisible(false);
  };

  const handleApplyTemplate = async (templateName: string) => {
    setTemplateToApply(templateName);
    setApplyTemplatePopupVisible(true);
  };

  const handleApplyTemplateConfirm = async () => {
    if (!templateToApply) return;

    try {
      setLoading(true);
      setApplyTemplatePopupVisible(false);
      // Fetch the template data
      const template = await maintenanceService.getMaintenanceTemplate(templateToApply);

      // Update maintenance message with template's defaultMessage and localizedMessages
      // Keep existing status and timestamps
      const updatedMessage: IMaintenanceFile = {
        ...maintenanceMessage,
        defaultMessage: template.defaultMessage,
        localizedMessages: template.localizedMessages,
      };

      await maintenanceService.updateMaintenanceMessage(updatedMessage);
      setAlertMessage(`Template "${templateToApply}" applied successfully.`);
      setAlertSeverity("success");
      await fetchMaintenanceMessage();
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Failed to apply template.");
      setAlertSeverity("error");
    } finally {
      setLoading(false);
      setTemplateToApply(undefined);
    }
  };

  const handleStartMaintenance = async () => {
    setStartMaintenancePopupVisible(true);
  };

  const handleStartMaintenanceConfirm = async () => {
    const envText = selectedEnvironment ? ` for environment "${selectedEnvironment.name}"` : "";

    try {
      setLoading(true);
      setStartMaintenancePopupVisible(false);
      if (selectedEnvironment && selectedEnvironment?.stage >= 8) {
        await maintenanceService.startMaintenance();
      } else {
        await maintenanceService.startMaintenance(selectedEnvironment?.name);
      }
      setAlertMessage(`Maintenance started${envText}.`);
      setAlertSeverity("success");
      await fetchMaintenanceMessage();
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Failed to start maintenance.");
      setAlertSeverity("error");
    } finally {
      setLoading(false);
    }
  };

  const handleEndMaintenance = async () => {
    setEndMaintenancePopupVisible(true);
  };

  const handleEndMaintenanceConfirm = async () => {
    const envText = selectedEnvironment ? ` for environment "${selectedEnvironment.name}"` : "";

    try {
      setLoading(true);
      setEndMaintenancePopupVisible(false);
      await maintenanceService.endMaintenance(selectedEnvironment?.name);
      setAlertMessage(`Maintenance ended${envText}.`);
      setAlertSeverity("success");
      await fetchMaintenanceMessage();
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Failed to end maintenance.");
      setAlertSeverity("error");
    } finally {
      setLoading(false);
    }
  };

  // background color coding for stg and prd environments
  const getBackgroundColor = (): string | undefined => {
    const envFromSelector = context?.selectedEnvironment;

    // No special background for dev environments
    if (!envFromSelector || envFromSelector.stage < 8) {
      return undefined;
    }

    const stageData = getEnvironmentStageData(envFromSelector?.stage);

    // using second color, first one is too dark and makes text hard to read
    return stageData[1];
  };

  // Only show EnvironmentPicker if EnvironmentSelector is not STAGING or PROD
  const shouldShowEnvironmentPicker = (): boolean => {
    const envFromSelector = context?.selectedEnvironment;
    if (!envFromSelector) {
      return true; // Show picker if no environment selected
    }
    // Hide picker for STAGING (8) and PROD (9)
    return envFromSelector.stage !== 8 && envFromSelector.stage !== 9;
  };

  return (
    <>
      <MaintenanceMessage
        maintenanceMessage={maintenanceMessage}
        selectedEnvironment={selectedEnvironment}
        onEnvironmentSelected={setSelectedEnvironment}
        onStartMaintenanceButtonClick={handleStartMaintenance}
        onEndMaintenanceButtonClick={handleEndMaintenance}
        onEditMessageButtonClick={handleEditClick}
        onRefresh={fetchMaintenanceMessage}
        backgroundColor={getBackgroundColor()}
        showEnvironmentPicker={shouldShowEnvironmentPicker()}
      />
      <MaintenanceTemplatesTable onApplyTemplate={handleApplyTemplate} />
      <MaintenanceMessageForm
        visible={formVisible}
        initialData={maintenanceMessage}
        onAccept={handleFormAccept}
        onDismiss={handleFormDismiss}
      />
      <YesNoCancelPopup
        visible={applyTemplatePopupVisible}
        title="Apply Template"
        message={`Apply template <b>"${templateToApply}"</b> to the maintenance message?`}
        onYes={handleApplyTemplateConfirm}
        onNo={() => {
          setApplyTemplatePopupVisible(false);
          setTemplateToApply(undefined);
        }}
      />
      <YesNoCancelPopup
        visible={startMaintenancePopupVisible}
        title="Start Maintenance"
        message={`Start maintenance${selectedEnvironment ? ` for environment <b>"${selectedEnvironment.name}"</b>` : ""}?`}
        onYes={handleStartMaintenanceConfirm}
        onNo={() => setStartMaintenancePopupVisible(false)}
      />
      <YesNoCancelPopup
        visible={endMaintenancePopupVisible}
        title="End Maintenance"
        message={`End maintenance${selectedEnvironment ? ` for environment <b>"${selectedEnvironment.name}"</b>` : ""}?`}
        onYes={handleEndMaintenanceConfirm}
        onNo={() => setEndMaintenancePopupVisible(false)}
      />
      {alertMessage && <Notify severity={alertSeverity} message={alertMessage} />}
      {loading && <LoadingOverlay />}
    </>
  );
};

export default MaintenanceManagement;
